import type { DelegatedParent } from "../types";
import type { TeamPiece } from "../hooks/useTeamBoard";
import { firstName } from "../lib/personColor";

export interface TeamParentCardProps {
  parent: DelegatedParent;
  pieces: TeamPiece[];
  /** The signed-in manager's own user id — a piece is tappable to
   * complete/reopen ONLY when it's theirs (design-spec-today-card.md's
   * A6 task brief: "Tapping own pieces completes them; members complete
   * via Today as usual"). Every other piece is read-only here. */
  meId: string;
  onTogglePiece: (piece: TeamPiece) => void;
  onMarkDone: () => void;
  onUndoDone: () => void;
}

export function TeamParentCard({
  parent,
  pieces,
  meId,
  onTogglePiece,
  onMarkDone,
  onUndoDone,
}: TeamParentCardProps) {
  const pct = parent.total > 0 ? Math.round((parent.done / parent.total) * 100) : 0;
  const isDone = parent.status === "done";

  return (
    <div class="team-parent-card" data-parent-id={parent.parent_assignment_id}>
      <div class="parent-card-top">
        <div>
          <div class="parent-title">{parent.title}</div>
          <div class="parent-sub">
            Handed out by {firstName(parent.assigned_by_name) || "someone"}
          </div>
        </div>
        <span class="parent-badge" role="status" aria-live="polite">
          {isDone ? "Done" : `${parent.done} of ${parent.total} done`}
        </span>
      </div>
      <div class="parent-progress-track">
        <div class="parent-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      {pieces.length > 0 ? (
        <ul class="team-piece-list">
          {pieces.map(({ task, done }) => {
            const isMine = task.assignee_id === meId;
            // Both branches below (the tappable button and the read-only
            // `<li>`) carry their own full `aria-label` on the OUTER
            // element already — hide these inner spans from the
            // accessibility tree so a screen reader never reads the same
            // title/assignee twice.
            const row = (
              <>
                <span class={`piece-dot${done ? " is-done" : ""}`} aria-hidden="true" />
                <span class={`piece-name${done ? " is-done" : ""}`} aria-hidden="true">
                  {task.title}
                </span>
                <span class="piece-assignee" aria-hidden="true">
                  {firstName(task.assignee_name)}
                </span>
              </>
            );
            return isMine ? (
              <li key={task.id}>
                <button
                  type="button"
                  class="team-piece-row is-tappable"
                  aria-pressed={done}
                  aria-label={done ? `Mark '${task.title}' not done` : `Mark '${task.title}' done`}
                  onClick={() => onTogglePiece({ task, done })}
                >
                  {row}
                </button>
              </li>
            ) : (
              <li
                key={task.id}
                class="team-piece-row"
                aria-label={`${task.title} — ${done ? "done" : "not done"} — ${firstName(task.assignee_name)}`}
              >
                {row}
              </li>
            );
          })}
        </ul>
      ) : null}

      <button type="button" class="secondary-btn" onClick={isDone ? onUndoDone : onMarkDone}>
        {isDone ? "Reopen the whole thing" : "Mark the whole thing done"}
      </button>
      {!isDone ? (
        <p class="footer-hint">
          You can do this any time — even if not every piece is checked off.
        </p>
      ) : null}
    </div>
  );
}
